import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'About Us', href: '/about' },
    { label: 'Locations', href: '/locations' },
  ];

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 cursor-pointer">
              <img
                src="https://mrtintofamerica.com/wp-content/uploads/2024/11/MR-TINT-LOGO-Whitetxt.png"
                alt="Mr. Tint of America"
                className="h-16 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors tracking-wide whitespace-nowrap cursor-pointer relative group ${
                    isActive(link.href) ? 'text-white' : 'text-white/70 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-[#FF6B35] transition-all duration-300 ${
                      isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </Link>
              ))}
            </div>

            {/* Right CTAs */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href="tel:+15612759473"
                className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-[#FF6B35] text-base" />
                </div>
                (561) 275-9473
              </a>
              <Link
                to="/#quote"
                className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-6 py-2.5 rounded-full text-sm font-bold tracking-wide transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Fast Quote
              </Link>
            </div>

            {/* Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[#0A0A0A] border-t border-white/10 animate-slide-down">
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block text-sm font-medium py-2 border-b border-white/5 cursor-pointer transition-colors ${
                    isActive(link.href) ? 'text-[#FF6B35]' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:+15612759473"
                className="flex items-center gap-2 text-white/70 py-2 text-sm cursor-pointer"
              >
                <i className="ri-phone-fill text-[#FF6B35]" />
                (561) 275-9473
              </a>
              <Link
                to="/#quote"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-[#FF6B35] text-white text-center py-3 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                Get Fast Quote
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Sticky bottom CTA bar (mobile) */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 flex gap-3">
          <a
            href="tel:+15612759473"
            className="flex-1 border border-white/20 text-white text-center py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 cursor-pointer"
          >
            <i className="ri-phone-fill text-[#FF6B35]" />
            Call Now
          </a>
          <Link
            to="/#quote"
            className="flex-1 bg-[#FF6B35] text-white text-center py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap flex items-center justify-center"
          >
            Get Quote
          </Link>
        </div>
      </div>
    </>
  );
}
